import React, { useEffect, useState } from "react";
import { 
  FiArrowRight, 
  FiShield, 
  FiZap, 
  FiChevronDown,
  FiFileText,
  FiMessageSquare,
  FiUsers
} from "react-icons/fi";
import UploadSection from "../components/UploadSection";
import ManualSection from "../components/ManualSection";
import Footer from "../components/Footer";

const Home = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToUpload = () => {
    document.getElementById('upload-section')?.scrollIntoView({ 
      behavior: 'smooth' 
    });
  };

  const features = [
    {
      icon: <FiFileText className="w-6 h-6" />,
      title: "Support multi-format",
      description: "Analyse de documents PDF, DOCX et TXT"
    },
    {
      icon: <FiZap className="w-6 h-6" />,
      title: "Traitement rapide",
      description: "Génération de résumés en quelques instants"
    },
    {
      icon: <FiMessageSquare className="w-6 h-6" />,
      title: "Chat avec l'IA",
      description: "Dialoguez avec vos documents pour approfondir"
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: "Confidentialité",
      description: "Vos documents sont traités de manière sécurisée"
    }
  ];

  const useCases = [
    {
      title: "Professionnels",
      description: "Analysez vos rapports, contrats et documents professionnels",
      icon: "💼"
    },
    {
      title: "Étudiants & Chercheurs",
      description: "Résumez vos articles académiques et thèses",
      icon: "🎓"
    },
    {
      title: "Entreprises",
      description: "Optimisez la gestion documentaire de votre équipe",
      icon: "🏢"
    }
  ];

  return (
    <div className="font-sans bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-20 pb-16 md:pt-28 md:pb-24">
        {/* Background minimal */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-white"></div>
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Logo/Tagline */}
            <div className="mb-10">
              <div className="inline-flex items-center gap-3 px-5 py-3 bg-teal-50 rounded-full mb-6">
                <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
                <span className="text-sm font-medium text-teal-700">
                  IA de résumé de documents
                </span>
              </div>
            </div>

            {/* Main Title */}
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="block text-gray-900">Résumé intelligent</span>
              <span className="block text-teal-600">de vos documents</span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-10 leading-relaxed">
              Transformez vos PDF, Word et fichiers texte en résumés clairs et structurés 
              grâce à notre technologie d'intelligence artificielle.
            </p>

            {/* Primary Action */}
            <div className="mb-16">
              <button
                onClick={scrollToUpload}
                className="group px-8 py-4 bg-teal-600 text-white font-semibold rounded-xl hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 flex items-center justify-center gap-3 mx-auto"
              >
                <span>Commencer</span>
                <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            {/* Features Grid */}
            {/* <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {features.map((feature, index) => (
                  <div 
                    key={index}
                    className="p-4 bg-white rounded-lg border border-gray-200 hover:border-teal-300 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center mb-3">
                      <div className="text-teal-600">
                        {feature.icon}
                      </div>
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm mb-1">{feature.title}</h3>
                    <p className="text-xs text-gray-500">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div> */}

            {/* Scroll Indicator */}
            <div className="mt-12">
              <button
                onClick={scrollToUpload}
                className="flex flex-col items-center text-gray-400 hover:text-teal-500 transition-colors"
              >
                <span className="text-sm mb-1">Continuer</span>
                <FiChevronDown size={20} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Upload Section */}
      <section 
        id="upload-section" 
        className="scroll-mt-20 bg-gray-50 py-12"
        style={{ scrollMarginTop: '5rem' }}
      >
        <div className="max-w-6xl mx-auto px-4">
          <UploadSection />
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Pour qui ?
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              SumDocx s'adapte aux besoins de différents profils d'utilisateurs
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="p-6 bg-gray-50 rounded-xl hover:bg-teal-50 transition-colors group">
                <div className="text-3xl mb-4">{useCase.icon}</div>
                <h4 className="text-xl font-semibold text-gray-900 mb-3">{useCase.title}</h4>
                <p className="text-gray-600">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Manual Section */}
      <ManualSection />


      {/* Final Section */}
      <section className="py-20 px-4 bg-white border-t border-gray-200">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-20 h-20 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-6">
            <FiFileText className="text-teal-600 w-10 h-10" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Prêt à essayer ?
          </h2>
          <p className="text-gray-600 mb-10 max-w-2xl mx-auto">
            Découvrez comment SumDocx peut vous aider à gagner du temps et à mieux comprendre vos documents.
          </p>
          <button
            onClick={scrollToUpload}
            className="px-8 py-4 bg-teal-600 text-white font-semibold rounded-xl hover:bg-teal-700 transition-colors shadow-lg hover:shadow-xl"
          >
            Analyser un document
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;